import { Platform } from '@angular/cdk/platform';
import { DatePipe, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { environment } from '@environments/environment';
import { provideLocaleConfig } from '@locale/locale-config.provider';
import { provideTranslocoConfig } from '@locale/transloco.provider';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { moduleRegistry } from '@modules/forms/form-modules/module-registry';
import { PatientNamePipe } from '@modules/patient/patient-name.pipe';
import { SyncCenterBridgeService } from '@modules/video-upload/sync-center-bridge.service';
import { WA_CSS } from '@ng-web-apis/common';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideModuleRegistry } from '@pixacare/forms-angular';
import { localeInterceptor } from '@shared/interceptors/locale.interceptor';
import { responseDateInterceptor } from '@shared/interceptors/response-date.interceptor';
import { serverUnavailableInterceptor } from '@shared/interceptors/server-unavailable.interceptor';
import { timeZoneInterceptor } from '@shared/interceptors/time-zone.interceptor';
import { unauthorizedInterceptor } from '@shared/interceptors/unauthorized.interceptor';
import { withCredentialsInterceptor } from '@shared/interceptors/with-credentials.interceptor';
import { JoinPipe } from '@shared/pipes/join.pipe';
import { loadAppInitializationData } from '@shared/utils/app-initialization';
import { DateTimeTransformer } from '@shared/utils/date-time-transformer';
import { DateTransformer } from '@shared/utils/date-transformer';
import { iconsFactory } from '@shared/utils/icons-factory';
import { validationErrorFactory } from '@shared/utils/validation-errors-factory';
import {
  provideTaiga,
  TUI_ANCHOR_SUPPORT,
  TUI_ASSETS_PATH,
  TUI_ICON_RESOLVER,
  TUI_TIMELINE_SUPPORT,
  TUI_VALIDATION_ERRORS,
  tuiButtonOptionsProvider,
  tuiHintOptionsProvider,
} from '@taiga-ui/core';
import { tuiInputDateOptionsProvider, tuiInputDateTimeOptionsProvider } from '@taiga-ui/kit';
import { tuiHeaderOptionsProvider } from '@taiga-ui/layout';
import player, { LottiePlayer } from 'lottie-web/build/player/lottie_svg';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideLottieOptions } from 'ngx-lottie';
import { routes } from './app.routes';
import { GlobalErrorHandler } from './helpers/global-error-handler';
import { registerAvailableLocales } from './locale/utils/locale-registration';
import { AppConfigHttpService } from './services/http/app-config.http.service';
import { provideStoreFeatures } from './shared/store/store-features.provider';

const playerFactory = (): LottiePlayer => player;

// Safari accepts `animation-timeline` but Taiga's named scroll timelines don't drive its scrollbar thumbs there: they stay
// visible at their default size on every textfield and list. Taiga's JS scrollbar only draws a thumb when there is
// something to scroll.
// TODO(taiga): drop the `TUI_TIMELINE_SUPPORT` provider once Taiga's default accounts for Safari.
const isScrollTimelineSupported = (): boolean =>
  !inject(Platform).SAFARI && !!inject(WA_CSS)?.supports('animation-timeline', '--name');

// Safari recalculates the offset of an anchor-positioned dropdown on the main thread, so the dropdown lags behind its
// anchor and feels slow. Taiga's JS positioning places it without that cost.
// TODO(taiga): drop the `TUI_ANCHOR_SUPPORT` provider once Taiga's anchored dropdown holds up on Safari.
const isAnchorPositioningSupported = (): boolean =>
  !inject(Platform).SAFARI && !!inject(WA_CSS)?.supports('anchor-name', '--name');

const appInsightsFactory = (): ApplicationInsights =>
  new ApplicationInsights({
    config: {
      instrumentationKey: environment.appInsights.instrumentationKey,
    },
  });

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: TUI_TIMELINE_SUPPORT, useFactory: isScrollTimelineSupported },
    { provide: TUI_ANCHOR_SUPPORT, useFactory: isAnchorPositioningSupported },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideAppInitializer(() => registerAvailableLocales()),
    provideAppInitializer(() => loadAppInitializationData(inject(AppConfigHttpService), inject(AuthenticationService))),
    provideAppInitializer(() => {
      inject(SyncCenterBridgeService);
    }),
    provideStore(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
      },
    ),
    provideStoreFeatures(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
    }),
    provideTranslocoConfig(),
    {
      provide: ApplicationInsights,
      useFactory: appInsightsFactory,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    tuiInputDateTimeOptionsProvider({
      valueTransformer: new DateTimeTransformer(),
    }),
    tuiInputDateOptionsProvider({
      valueTransformer: new DateTransformer(),
    }),
    {
      provide: TUI_ICON_RESOLVER,
      deps: [TUI_ASSETS_PATH],
      useFactory: iconsFactory,
    },
    {
      provide: TUI_VALIDATION_ERRORS,
      deps: [AppConfigHttpService],
      useFactory: validationErrorFactory,
    },
    tuiButtonOptionsProvider({
      size: 'm',
    }),
    tuiHintOptionsProvider({
      appearance: 'floating',
    }),
    tuiHeaderOptionsProvider({
      size: 'h4',
    }),
    provideHttpClient(
      withXhr(),
      withInterceptors([
        unauthorizedInterceptor,
        responseDateInterceptor,
        serverUnavailableInterceptor,
        localeInterceptor,
        timeZoneInterceptor,
        withCredentialsInterceptor,
      ]),
    ),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),
    provideAuth(() => getAuth()),
    provideLottieOptions({
      player: playerFactory,
    }),
    provideTaiga({ mode: 'light' }),
    provideLocaleConfig(),
    provideCharts(withDefaultRegisterables()),
    provideModuleRegistry(moduleRegistry),
    DatePipe,
    TitleCasePipe,
    UpperCasePipe,
    PatientNamePipe,
    JoinPipe,
  ],
};
